import { Badge, Banner, Button, Field, Input } from '@cloudflare/kumo';
import { FormPage, FormSection } from '../components/ui/FormPage';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DataTable as Table } from '../components/ui/DataTable';
import { ICONS } from '../components/ui/icons';
import { SearchField } from '../components/ui/SearchField';
import { useTenant } from '../contexts/TenantContext';
import { useAppToast } from '../hooks/useAppToast';
import { useAuthenticatedFetch } from '../hooks/useAuthenticatedFetch';
import { DangerConfirmModal } from '../components/DangerConfirmModal';
import { RowActions } from '../components/atlas/RowActions';
import { KeyValueTag, ManageTagsModal, TagsCell, tagText } from '../components/atlas/KeyValueTags';
import { SortHeader, SortState, TablePager, sortBy } from '../components/atlas/DataTableParts';
import { SkeletonText } from '../components/ui/Skeleton';

interface ProjectRow {
  id: string;
  name: string;
  isDefault: boolean;
  tags: KeyValueTag[];
  createdAt: string;
  usage: {
    contacts: number;
    interactiveCampaigns: number;
    connections: number;
    sessions: number;
    segments: number;
    categories: number;
    templates: number;
    automations: number;
  };
}

type SortKey = 'name' | 'contacts' | 'campaigns' | 'connections' | 'createdAt';

type NameModal = { mode: 'create' } | { mode: 'rename'; project: ProjectRow } | null;

/**
 * Projetos da organização (como a lista "Projects" do MongoDB Atlas): cada
 * projeto tem os próprios contatos, campanhas, conexões e integrações.
 */
export function ProjectsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { selectedTenant, organizations, selectedTenantId, selectedProjectId, setSelectedProjectId, reloadContext, can } = useTenant();
  const fetchApi = useAuthenticatedFetch();
  const toast = useAppToast();

  const [projects, setProjects] = useState<ProjectRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [nameModal, setNameModal] = useState<NameModal>(null);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<ProjectRow | null>(null);
  const [tagging, setTagging] = useState<ProjectRow | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [sort, setSort] = useState<SortState<SortKey>>({ key: 'name', dir: 'asc' });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const canManage = can('projects:manage');
  const orgName = organizations.find((o) => o.id === selectedTenantId)?.name || selectedTenant?.name || 'Organização';

  const load = useCallback(async () => {
    try {
      const res = await fetchApi('/api/projects');
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Erro ao carregar os projetos');
      setProjects((data.projects || []).map((p: ProjectRow) => ({ ...p, tags: p.tags || [] })));
      setLoadError(false);
    } catch {
      setLoadError(true); // como no Atlas: aviso em vermelho acima da tabela
    } finally {
      setLoading(false);
    }
  }, [fetchApi, toast]);
  useEffect(() => { load(); }, [load]);

  // /projetos?novo=1 (atalho do seletor da barra superior)
  useEffect(() => {
    if (searchParams.get('novo') === '1' && canManage) {
      openCreate();
      searchParams.delete('novo');
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, canManage]);

  const openCreate = () => { setName(''); setError(null); setNameModal({ mode: 'create' }); };
  const openRename = (project: ProjectRow) => { setName(project.name); setError(null); setNameModal({ mode: 'rename', project }); };

  const submitName = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nameModal) return;
    setSaving(true);
    setError(null);
    try {
      const res = nameModal.mode === 'create'
        ? await fetchApi('/api/projects', { method: 'POST', body: JSON.stringify({ name }) })
        : await fetchApi(`/api/projects/${nameModal.project.id}`, { method: 'PATCH', body: JSON.stringify({ name }) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Erro ao salvar o projeto');
      setNameModal(null);
      await Promise.all([load(), reloadContext()]);
      if (nameModal.mode === 'create') {
        toast.success(`Projeto ${data.project.name} criado`);
        // Como no Atlas: o projeto novo já abre
        setSelectedProjectId(data.project.id);
        navigate('/dashboard');
      } else {
        toast.success('Projeto renomeado');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar o projeto');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    const project = pendingDelete;
    setPendingDelete(null);
    try {
      const res = await fetchApi(`/api/projects/${project.id}`, { method: 'DELETE' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Erro ao excluir o projeto');
      toast.success(`Projeto ${project.name} excluído`);
      if (project.id === selectedProjectId) {
        const fallback = projects.find((p) => p.isDefault);
        if (fallback) setSelectedProjectId(fallback.id);
      }
      await Promise.all([load(), reloadContext()]);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao excluir o projeto');
    }
  };

  const open = (project: ProjectRow) => {
    if (project.id !== selectedProjectId) setSelectedProjectId(project.id);
    navigate('/dashboard');
  };

  const saveTags = async (project: ProjectRow, tags: KeyValueTag[]) => {
    const res = await fetchApi(`/api/projects/${project.id}`, { method: 'PATCH', body: JSON.stringify({ tags }) });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.message || 'Erro ao salvar as tags');
    setProjects((list) => list.map((p) => (p.id === project.id ? { ...p, tags: data.project.tags } : p)));
    toast.success('Tags salvas');
  };

  const copyId = async (project: ProjectRow) => {
    try { await navigator.clipboard.writeText(project.id); toast.success('ID do projeto copiado'); } catch { toast.error('Não foi possível copiar'); }
  };

  // Busca pelo nome ou por uma tag ("ambiente", "produção" ou "ambiente: produção")
  const q = search.trim().toLowerCase();
  const filtered = projects.filter((p) => !q || p.name.toLowerCase().includes(q) || p.tags.some((t) => tagText(t).toLowerCase().includes(q)));
  const sorted = sortBy(filtered, sort, (p, k) => {
    if (k === 'name') return p.name;
    if (k === 'contacts') return p.usage.contacts;
    if (k === 'campaigns') return p.usage.interactiveCampaigns;
    if (k === 'connections') return p.usage.sessions + p.usage.connections;
    return p.createdAt;
  });
  const pages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const current = Math.min(page, pages);
  const visible = sorted.slice((current - 1) * pageSize, current * pageSize);
  const totalResources = (p: ProjectRow) => Object.values(p.usage).reduce((a, b) => a + b, 0);

  if (nameModal !== null) {
    return (
      <FormPage
        backTo="/projetos"
        backLabel="Projetos"
        onBack={() => setNameModal(null)}
        title={nameModal.mode === 'rename' ? 'Renomear projeto' : 'Novo projeto'}
        description={nameModal.mode === 'rename'
          ? 'O nome aparece para todos os membros da organização.'
          : 'O projeto começa vazio: contatos, campanhas, conexões e integrações são configurados dentro dele.'}
        formId="project-form"
        submitLabel={nameModal.mode === 'rename' ? 'Salvar' : 'Criar projeto'}
        submitting={saving}
        submitDisabled={name.trim().length < 2}
        onCancel={() => setNameModal(null)}
      >
        <form id="project-form" onSubmit={submitName}>
          <FormSection title="Detalhes">
            {error && <Banner variant="error" description={<>{error}</>} />}
            <Field label="Nome do projeto">
              <Input onChange={(e) => setName(e.target.value)} placeholder="Ex.: Marketing" value={name} autoFocus required={true} />
            </Field>
          </FormSection>
        </form>
      </FormPage>
    );
  }

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '28px 40px 0', marginBottom: 24 }}>
        <div>
          <h2 className="text-xl font-semibold" style={{ marginBottom: 4, fontSize: 24, fontWeight: 600 }}>
            Projetos
          </h2>
          <div style={{ color: 'var(--text-muted)' }}>
            Cada projeto de <strong>{orgName}</strong> tem os próprios contatos, campanhas, conexões e integrações.
          </div>
        </div>
        {canManage && (
          <Button variant="primary" icon={ICONS.Plus} onClick={openCreate}>
            Novo projeto
          </Button>
        )}
      </div>

      <div className="page-content space-y-4">
        <div className="max-w-md">
          <SearchField
            ariaLabel="Buscar projetos"
            placeholder="Buscar projeto ou tag..."
            value={search}
            onChange={(value) => { setSearch(value); setPage(1); }}
          />
        </div>
        {loadError && <div className="atlas-error">Não foi possível carregar os projetos. Tente de novo.</div>}

        <div className="bg-white rounded-lg border border-[var(--border)] overflow-hidden">
          {loading ? (
            <div className="py-8"><SkeletonText lines={4} /></div>
          ) : visible.length === 0 ? (
            <div className="text-center py-12"><div className="!text-[var(--text-faint)]">Nenhum projeto encontrado.</div></div>
          ) : (
            <div className="overflow-x-auto">
              <Table
                variant="borderless"
                items={visible}
                columnDefinitions={[
                  {
                    id: 'projeto',
                    header: <SortHeader label="Projeto" sortKey="name" sort={sort} onSort={setSort} />,
                    cell: (p: ProjectRow) => (
                      <div className="flex items-center gap-2">
                        <button type="button" className="project-link" onClick={() => open(p)}>{p.name}</button>
                        {p.isDefault && <Badge variant="neutral">Padrão</Badge>}
                        {p.id === selectedProjectId && <Badge variant="green">Ativo</Badge>}
                      </div>
                    ),
                  },
                  {
                    id: 'contatos',
                    header: <SortHeader label="Contatos" sortKey="contacts" sort={sort} onSort={setSort} />,
                    cell: (p: ProjectRow) => p.usage.contacts.toLocaleString('pt-BR'),
                  },
                  {
                    id: 'campanhas',
                    header: <SortHeader label="Campanhas" sortKey="campaigns" sort={sort} onSort={setSort} />,
                    cell: (p: ProjectRow) => p.usage.interactiveCampaigns.toLocaleString('pt-BR'),
                  },
                  {
                    id: 'tags',
                    header: (
                      <span className="atlas-th-info" title="Pares de chave e valor para organizar e filtrar projetos. A busca acima também procura nas tags.">
                        Tags <ICONS.InfoWithCircle size={16} />
                      </span>
                    ),
                    cell: (p: ProjectRow) => <TagsCell tags={p.tags} canEdit={canManage} onManage={() => setTagging(p)} />,
                  },
                  {
                    id: 'conexoes',
                    header: <SortHeader label="Conexões" sortKey="connections" sort={sort} onSort={setSort} />,
                    cell: (p: ProjectRow) => (p.usage.sessions + p.usage.connections).toLocaleString('pt-BR'),
                  },
                  {
                    id: 'criado-em',
                    header: <SortHeader label="Criado em" sortKey="createdAt" sort={sort} onSort={setSort} />,
                    cell: (p: ProjectRow) => <div className="!text-[var(--text-muted)] text-xs">{new Date(p.createdAt).toLocaleDateString('pt-BR')}</div>,
                  },
                  {
                    id: 'acoes',
                    header: 'Ações',
                    cell: (p: ProjectRow) => (
                      <RowActions
                        label={p.name}
                        actions={[
                          { label: 'Copiar ID do projeto', onClick: () => copyId(p) },
                          { label: 'Abrir projeto', onClick: () => open(p) },
                          ...(canManage ? [
                            { label: 'Editar nome', onClick: () => openRename(p) },
                            { label: 'Gerenciar tags', onClick: () => setTagging(p) },
                          ] : []),
                        ]}
                        onDelete={canManage && !p.isDefault ? () => setPendingDelete(p) : undefined}
                        deleteDisabledReason={totalResources(p) > 0 ? 'Esvazie o projeto para excluir' : null}
                      />
                    ),
                  },
                ]}
                ariaLabels={{ tableLabel: 'Projetos', selectionGroupLabel: 'Projetos' }}
              />
            </div>
          )}
          {!loading && filtered.length > 0 && (
            <TablePager page={current} pageSize={pageSize} total={filtered.length} onPage={setPage} onPageSize={setPageSize} />
          )}
        </div>
      </div>



      <ManageTagsModal
        open={tagging !== null}
        subject={tagging?.name || ''}
        tags={tagging?.tags || []}
        onClose={() => setTagging(null)}
        onSave={(tags) => saveTags(tagging!, tags)}
      />

      <DangerConfirmModal
        open={pendingDelete !== null}
        title="Excluir projeto"
        confirmText="Excluir projeto"
        objectName={pendingDelete?.name}
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      >
        O projeto <strong>{pendingDelete?.name}</strong> e as integrações dele serão excluídos.
      </DangerConfirmModal>
    </>
  );
}
